import { toast } from "react-toastify";
import { CHAT_TEXT_CONSTANTS } from "../utils/constants";
import { normalizeAblyData } from "../services/chatRealtimeService";

const TOAST_COALESCE_MS = 400;

type PendingChatToast = {
  timeoutId: ReturnType<typeof setTimeout>;
  senderName?: string;
  onClick?: () => void;
};

const pendingChatToasts = new Map<string, PendingChatToast>();

export function formatNewChatMessageToast(senderName?: string): string {
  const sender =
    senderName?.trim() || CHAT_TEXT_CONSTANTS.DEFAULT_CHAT_USER_NAME;
  return `${CHAT_TEXT_CONSTANTS.NEW_MESSAGE} from ${sender}`;
}

export function showNewChatMessageToast(
  message: string,
  onClick?: () => void
): void {
  toast.info(message, {
    position: "top-center",
    closeButton: false,
    onClick,
  });
}

/** Coalesces Ably + FCM alerts for the same room into one toast. */
export function scheduleNewChatMessageToast(
  roomId: string,
  options: {
    senderName?: string;
    onClick?: () => void;
  }
): void {
  const id = roomId.trim();
  if (!id) {
    return;
  }

  const existing = pendingChatToasts.get(id);

  if (existing) {
    clearTimeout(existing.timeoutId);
  }

  const merged: Omit<PendingChatToast, "timeoutId"> = {
    senderName: options.senderName?.trim() || existing?.senderName,
    onClick: options.onClick ?? existing?.onClick,
  };

  const timeoutId = setTimeout(() => {
    pendingChatToasts.delete(id);
    showNewChatMessageToast(
      formatNewChatMessageToast(merged.senderName),
      merged.onClick
    );
  }, TOAST_COALESCE_MS);

  pendingChatToasts.set(id, { ...merged, timeoutId });
}

export function extractSenderNameFromNotificationEvent(
  raw: unknown
): string | undefined {
  const normalized = normalizeAblyData(raw);
  if (!normalized || typeof normalized !== "object") {
    return undefined;
  }
  const roots = [normalized as Record<string, unknown>];
  const payload = (normalized as Record<string, unknown>).payload;
  if (payload && typeof payload === "object") {
    roots.push(payload as Record<string, unknown>);
  }
  for (const obj of roots) {
    const name = obj.senderName;
    if (typeof name === "string" && name.trim()) {
      return name.trim();
    }
  }
  return undefined;
}
